import React, {useState} from 'react'
import PageLayout from '../../components/layout/PageLayout'
import WizButton from '../../components/button/WizButton'
import Text from '../../components/text/Text'
import FeedList from './FeedList'
import FeedForm from './FeedForm'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import ConfirmSubmitModal from './ConfirmSubmitModal'
import ConfirmActiveModal from './ConfirmActiveModal'
import {AI_FEED_CATEGORY_LABEL, emptyFormBody, getFeedChannel} from './constants'
import {formBodyToCreateTemplate, formBodyToUpdateTemplate} from './utils'
import {AiFeedCategory, IFeedItem, IFormBody} from './types'
import useQueryFeedTemplateKinds from '../../hooks/api/feed/useQueryFeedTemplateKinds'
import useQueryFeedTemplates from '../../hooks/api/feed/useQueryFeedTemplates'
import {createFeedTemplate} from '../../apis/feed/createFeedTemplate'
import {updateFeedTemplate} from '../../apis/feed/updateFeedTemplate'
import {deleteFeedTemplate} from '../../apis/feed/deleteFeedTemplate'
import {setFeedTemplateActive} from '../../apis/feed/setFeedTemplateActive'

interface IProps {
    className?: string
}

export function Feed({className}: IProps) {
    // 계약(kinds)에서 canonical kind 목록을 얻어 kind별로 템플릿을 조회한다. kind가 곧 카테고리.
    const {contract} = useQueryFeedTemplateKinds()
    const kinds = Object.keys(contract.kinds)
    const {items, refetch} = useQueryFeedTemplates(kinds)

    // 생성 시 카테고리 선택지는 계약 kind에서 파생(하드코딩 금지). 라벨은 어드민 관리, 없으면 kind 그대로.
    const categoryOptions = kinds.map(kind => ({
        id: kind as AiFeedCategory,
        value: AI_FEED_CATEGORY_LABEL[kind as AiFeedCategory] ?? kind,
    }))

    const [view, setView] = useState<'list' | 'create' | 'edit'>('list')
    const [body, setBody] = useState<IFormBody>(emptyFormBody())
    const [deleteTarget, setDeleteTarget] = useState<IFeedItem | null>(null)
    const [activeTarget, setActiveTarget] = useState<IFeedItem | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const openCreate = () => {
        setBody(emptyFormBody())
        setView('create')
    }

    // kind/language는 불변이라 category는 수정 화면에서 고정(비활성). 문구(content_blocks)만 편집한다.
    const openEdit = (item: IFeedItem) => {
        setBody({
            id: item.id,
            post_type: item.post_type,
            category: item.category,
            content_blocks: item.content_blocks,
        })
        setView('edit')
    }

    const onDelete = (item: IFeedItem) => {
        setDeleteTarget(item)
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        const res = await deleteFeedTemplate(deleteTarget.id)
        setDeleteTarget(null)
        if (res?.error) {
            alert(res.error)
            return
        }
        refetch()
    }

    // 토글 클릭 → 확인 모달만 연다. 실제 반영은 confirmToggleActive에서.
    const onToggleActive = (item: IFeedItem) => setActiveTarget(item)

    const confirmToggleActive = async () => {
        if (!activeTarget) return
        const res = await setFeedTemplateActive(activeTarget.id, !activeTarget.active)
        setActiveTarget(null)
        if (res?.error) {
            alert(res.error)
            return
        }
        refetch()
    }

    // 저장 버튼 → 확인 모달을 연다.
    const onSubmit = () => {
        setConfirmOpen(true)
    }

    // 모달 확정 → 실제 생성/수정. id가 있으면 수정(PUT), 없으면 생성(POST). 둘 다 동일한 변수 계약으로 검증(code 5050).
    const confirmSubmit = async () => {
        const res = body.id
            ? await updateFeedTemplate(body.id, formBodyToUpdateTemplate(body))
            : await createFeedTemplate(formBodyToCreateTemplate(body, getFeedChannel()))
        setConfirmOpen(false)
        // 실패(계약 밖 변수/미존재 id 등, code 5050)는 사유를 alert로 안내하고 폼에 머무른다.
        if (res?.error) {
            alert(res.error)
            return
        }
        setView('list')
        refetch()
    }

    return (
        <PageLayout title={'Feed'} className={`${className}`}>
            <Text className={'text-body2 text-font03'}>
                AI Feed 템플릿(auto_generated) 목록입니다. 현재 채널(언어)의 템플릿만 표시하며, 하나의 kind에 여러
                템플릿을 활성으로 등록하면 발행 시 무작위로 하나가 선택됩니다.
            </Text>

            {view === 'list' ? (
                <>
                    <div className={'mt-[20px] flex items-center justify-end'}>
                        <WizButton type={'primary'} text={'새 피드 만들기'} onClick={openCreate} />
                    </div>

                    <FeedList
                        className={'mt-[20px]'}
                        postType={'auto_generated'}
                        items={items}
                        onEdit={openEdit}
                        onDelete={onDelete}
                        onToggleActive={onToggleActive}
                    />
                </>
            ) : (
                <FeedForm
                    className={'mt-[20px]'}
                    view={view === 'edit' ? 'edit' : 'create'}
                    body={body}
                    setBody={setBody}
                    categoryOptions={categoryOptions}
                    contract={contract}
                    onSubmit={onSubmit}
                    onCancel={() => setView('list')}
                />
            )}

            <ConfirmDeleteModal item={deleteTarget} onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} />

            <ConfirmActiveModal
                item={activeTarget}
                onConfirm={confirmToggleActive}
                onCancel={() => setActiveTarget(null)}
            />

            <ConfirmSubmitModal
                open={confirmOpen}
                mode={view === 'edit' ? 'edit' : 'create'}
                body={body}
                onConfirm={confirmSubmit}
                onCancel={() => setConfirmOpen(false)}
            />
        </PageLayout>
    )
}
